/** The ten swatches every colour picker in this app offers.
 *
 *  They were declared in `constants/states.ts` as `STATE_COLOR_SWATCHES`, but
 *  four of the five things that pick from them are not states — job roles,
 *  request statuses and project states already shared the list, and NG-1594
 *  added work items. They live here so a consumer does not have to import a
 *  states module to colour something that is not a state.
 *
 *  This file is the R-T1 colour-literal ratchet's palette exception, inherited
 *  from `states.ts` along with the list (see `scripts/check-color-literals.sh`).
 *  These are product DATA, not design tokens: they are persisted via the API, so
 *  the values must stay stable across deploys. Changing one does NOT recolour
 *  rows already storing it — it only changes what the picker offers next.
 *
 *  The backend keeps its own copy for the sales seeder
 *  (`WORK_ITEM_COLOR_SWATCHES` in `work_items/models/choices.py`); nothing
 *  imports across the boundary, so `tests/work_items/test_work_item_color.py`
 *  pins these exact values to keep the two from drifting.
 */

/** One declaration, so a swatch cannot exist without a name or vice versa —
 *  the two used to be a parallel array and map with nothing checking they
 *  agreed, and the failure was a picker option whose trigger showed raw hex. */
const SWATCHES = [
  ["#6B7280", "common.color.gray"],
  ["#2563EB", "common.color.blue"],
  ["#16A34A", "common.color.green"],
  ["#EAB308", "common.color.yellow"],
  ["#F97316", "common.color.orange"],
  ["#DC2626", "common.color.red"],
  ["#A855F7", "common.color.purple"],
  ["#EC4899", "common.color.pink"],
  ["#0EA5E9", "common.color.sky"],
  ["#14B8A6", "common.color.teal"],
] as const;

export const COLOR_SWATCHES = SWATCHES.map(([hex]) => hex) as readonly TColorSwatch[];

export type TColorSwatch = (typeof SWATCHES)[number][0];

/** Grey. NOT what a new work item gets — see `randomColorSwatch` — but the
 *  neutral a control falls back to when it has no value yet, and the swatch
 *  every settings form starts on. Kept in step with the backend's
 *  `WORK_ITEM_DEFAULT_COLOR`. */
export const DEFAULT_COLOR_SWATCH: TColorSwatch = SWATCHES[0][0];

/** The colour a new work item starts on (NG-1594, product decision 2026-09-23).
 *
 *  Rolled in the create forms rather than left to the backend, so the author can
 *  SEE the colour their item will have — and change it — before saving, instead
 *  of finding out afterwards. The backend rolls its own when a client sends
 *  nothing, so the two agree without this being load-bearing.
 */
export function randomColorSwatch(): TColorSwatch {
  return COLOR_SWATCHES[Math.floor(Math.random() * COLOR_SWATCHES.length)];
}

/** A stored work-item colour, which the API validates as `#rrggbb` and nothing
 *  else (`HEX_COLOR_PATTERN` in `work_items/colors.py`). Matched
 *  case-insensitively because that validator is, so a row may hold either. */
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** Whether a value is a stored colour at all.
 *
 *  Lives here rather than beside the component that draws one, because it is
 *  palette knowledge and not rendering: the notification reader asks it of a
 *  payload key, with no intention of importing a React component to do so.
 */
export function isColorSwatch(value: unknown): value is string {
  return typeof value === "string" && HEX_COLOR.test(value);
}

const LABEL_KEY_BY_COLOR = new Map<string, string>(
  SWATCHES.map(([hex, labelKey]) => [hex, labelKey]),
);

/** The locale key for a stored colour, or `null` when it is not one of the ten.
 *
 *  A stored value CAN be outside the palette: the API validates only that a
 *  colour is `#rrggbb`, deliberately, so a row keeps working after the palette
 *  changes.
 *
 *  What a caller shows for one of those depends on what it is: a CONTROL, whose
 *  value you are about to change, shows the hex, because the exact code is the
 *  only honest thing to put in front of someone editing it (`WorkItemColorSelect`).
 *  A RECORD of something that already happened shows the word "Custom" — no
 *  surface prints a colour code any more (product, 2026-09-24, NG-1595), so a
 *  hex there would be the one place it leaked back in. `ColorValue` is that case.
 */
export function colorSwatchLabelKey(color: string): string | null {
  return LABEL_KEY_BY_COLOR.get(color.toUpperCase()) ?? null;
}
